import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CartRepository } from '@asm/features/sync/repositories/cart.repository';
import { Cart } from '@asm/features/quick-order/models/cart.model';
import { CouponRepository } from '@asm/features/sync/repositories/coupon.repository';
import { DefaultLocalCartService } from '@asm/features/sync/services/cart/default-local-cart.service';

export interface AvailableCouponDTO {
  code: string;
  name?: string;
  description?: string;
}

/**
 * Serviço de cupons offline.
 *
 * Replica a lógica do `DefaultCouponService` do backend, operando sobre o IndexedDB.
 *
 * - `getAvailableCoupons()`: lê o store `coupons` e retorna a lista.
 * - `applyCoupon()`: seta `cart.couponCode`, persiste e recalcula.
 * - `removeCoupon()`: limpa `cart.couponCode` e `cart.couponMessage`, persiste e recalcula.
 *
 * O vínculo Coupon → Promotion é por convenção de código:
 * cupom `code = X` ativa a promoção `code = X AND applyAutomatically = false`.
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalCouponService {
  private readonly couponRepo = inject(CouponRepository);
  private readonly cartRepo = inject(CartRepository);
  private readonly cartService = inject(DefaultLocalCartService);

  /** Retorna todos os cupons disponíveis sincronizados. */
  async getAvailableCoupons(): Promise<AvailableCouponDTO[]> {
    const coupons = await firstValueFrom(this.couponRepo.getAll());
    return coupons.map(c => ({ code: c.code, name: c.name, description: c.description }));
  }

  /**
   * Aplica um cupom ao carrinho e recalcula os preços.
   *
   * Valida que o cupom existe no store local antes de aplicar.
   * O recalculo via `DefaultLocalCartService.recalculateCart()` ativará
   * automaticamente a promoção vinculada ao cupom no motor offline.
   */
  async applyCoupon(cartUrl: string, couponCode: string, b2bUnitId: string): Promise<Cart> {
    const coupon = await firstValueFrom(this.couponRepo.getByCode(couponCode));
    if (!coupon) {
      throw new Error(`[CouponService] Cupom '${couponCode}' nao encontrado`);
    }

    const cart = await this.cartService.getCartFromUrl(cartUrl);
    if (!cart) {
      throw new Error('[CouponService] Carrinho nao encontrado');
    }

    const updated: Cart = { ...cart, couponCode };
    await firstValueFrom(this.cartRepo.addOrUpdate(updated));

    const recalculated = await this.cartService.recalculateCart(updated, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    return recalculated;
  }

  /**
   * Remove o cupom ativo do carrinho e recalcula.
   *
   * Limpa `couponCode` e `couponMessage` antes do recalculo,
   * garantindo que a promoção de cupom não seja mais avaliada.
   */
  async removeCoupon(cartUrl: string, b2bUnitId: string): Promise<Cart> {
    const cart = await this.cartService.getCartFromUrl(cartUrl);
    if (!cart) {
      throw new Error('[CouponService] Carrinho nao encontrado');
    }

    const updated: Cart = { ...cart, couponCode: undefined, couponMessage: undefined };
    await firstValueFrom(this.cartRepo.addOrUpdate(updated));

    const recalculated = await this.cartService.recalculateCart(updated, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    return recalculated;
  }
}
